import { useState } from 'react';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/_shared/components/ui/dialog';
import { Input } from '@/_shared/components/ui/input';
import { Label } from '@/_shared/components/ui/label';

interface StepUpPasswordDialogProps {
  /** What the password confirms, for example "Invite ada@acme.io". Null closes the dialog. */
  action: string | null;
  /** RudderStack's step-up scheme. Lookout can answer only `password`. */
  scheme: string | null;
  pending: boolean;
  error: string | null;
  onSubmit: (password: string) => void;
  onClose: () => void;
}

/**
 * RudderStack asks a password user for their password before an invitation change (its `SchemeGuard`). The password
 * lives in this dialog's state until it is sent once, and goes when the dialog closes.
 */
export function StepUpPasswordDialog({
  action,
  scheme,
  pending,
  error,
  onSubmit,
  onClose,
}: StepUpPasswordDialogProps): React.ReactElement {
  const [password, setPassword] = useState('');
  const open = action !== null;
  const supported = scheme === null || scheme === 'password';

  const close = (): void => {
    setPassword('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) close();
      }}
    >
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <form
          noValidate
          aria-label="Confirm your RudderStack password"
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (password && supported) onSubmit(password);
          }}
        >
          <DialogHeader>
            <DialogTitle>Confirm your RudderStack password</DialogTitle>
            <DialogDescription>
              {action ? `RudderStack asks for your password to continue: ${action}.` : ''}
            </DialogDescription>
          </DialogHeader>
          {!supported ? (
            <Callout variant="error">
              RudderStack asks for a check that Lookout cannot show. Make this change in the RudderStack web app.
            </Callout>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="step-up-password">Password</Label>
              <Input
                id="step-up-password"
                type="password"
                autoComplete="current-password"
                value={password}
                aria-invalid={error ? true : undefined}
                onChange={(event) => setPassword(event.target.value)}
              />
              {error ? (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              ) : null}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="secondary" disabled={pending} onClick={close} label="Cancel" />
            <Button
              type="submit"
              variant="primary"
              loading={pending}
              disabled={!password || !supported}
              label="Confirm"
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
